import { useEffect, useMemo, useState } from "react";
import { axiosBaseURL } from "../../../http";
import { Button, Col, Form, Row, Pagination, Collapse } from "react-bootstrap";
import { ArrowLeft, ArrowRight } from "react-bootstrap-icons";
import { toast } from "react-toastify";

const MAX_CHARS = 50;

export const Request = ({ request_id }) => {
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState(null); // null = loading
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(new Set()); // ids of expanded notes
  const pageSize = 3;

  useEffect(() => {
    axiosBaseURL
      .get(`request_api/request_notes/get_request_notes/?request_id=${request_id}`)
      .then((response) => {
        setNotes(Array.isArray(response.data) ? response.data : []);
        setPage(1);
        setExpanded(new Set()); // reset expansions on new data
      })
      .catch(() => setNotes([]));
  }, [request_id]);

  const totalPages = useMemo(
    () => (notes ? Math.max(1, Math.ceil(notes.length / pageSize)) : 1),
    [notes]
  );

  const pagedNotes = useMemo(() => {
    if (!notes) return [];
    const start = (page - 1) * pageSize;
    return notes.slice(start, start + pageSize);
  }, [notes, page]);

  const handleNoteSubmit = (e) => {
    e.preventDefault();
    console.log(note);
    axiosBaseURL.post("request_api/request_notes/create_note/",
      {
        note_type: 'user',
        request_id: request_id,
        note_text: note
      })
      .then(function (response) {
        setNote('')
        setNotes([response.data, ...notes])
        setPage(1)
      }).catch(function (error) {
        toast.error('Failed to post note.')
      });
  };

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div>
      <div className="mb-3">
        {notes === null && <p>Loading notes…</p>}

        {notes?.length === 0 && <p className="text-muted">No notes yet.</p>}

        {pagedNotes.map((n, i) => {
          const id = n.id ?? n.pk ?? i;
          const text = n.note_text ?? "";
          const isLong = text.length > MAX_CHARS;
          const isOpen = expanded.has(id);
          const head = isLong ? text.slice(0, MAX_CHARS) : text;
          const tail = isLong ? text.slice(MAX_CHARS) : "";

          return (
            <Row key={id} className="mb-2">
              <Col>
                <div className="border rounded p-2">
                  <div className="small text-muted mt-1">
                    {n.get_note_type_display === "Fullfiller" ? (
                      <ArrowRight color="red" />
                    ) : (
                      <ArrowLeft color="green" />
                    )}{" "}
                    {new Date(n.created).toLocaleString()}
                  </div>

                  <p
                    className="mb-1"
                    style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                    onClick={() => isLong && toggle(id)}
                    role={isLong ? "button" : undefined}
                  >
                    {head}
                    {!isOpen && isLong && "…"}
                  </p>

                  {isLong && (
                    <Collapse in={isOpen}>
                      <div id={`note-tail-${id}`}>
                        <p
                          className="mb-1"
                          style={{
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {tail}
                        </p>
                      </div>
                    </Collapse>
                  )}

                  {isLong && (
                    <div className="d-flex justify-content-end">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={() => toggle(id)}
                        aria-expanded={isOpen}
                        aria-controls={`note-tail-${id}`}
                      >
                        {isOpen ? "Show less" : "Show more"}
                      </Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          );
        })}

        {notes && notes.length > pageSize && (
          <div className="d-flex justify-content-end mt-2">
            <Pagination size="sm" className="mb-0">
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              />
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
                  {p}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            </Pagination>
          </div>
        )}
      </div>

      <Form onSubmit={handleNoteSubmit}>
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={3}
            required
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Write a reply..."
          />
        </Form.Group>
        <div className="d-flex justify-content-center m-2">
          <Button className="w-100" variant="outline-primary" type="submit">
            Reply
          </Button>
        </div>
      </Form>
    </div>
  );
};
